import { createAsyncThunk } from "@reduxjs/toolkit";
import { addPrinterDetails, deletePrintList, getMargin, getPrintList, getPrintSettings, 
        listHCFACoordinates, singleHCFACoordinates, updateMargin, updatePrintCoordinates, 
        updatePrintSettings, updateSingleHCFACoordinates } from "./service";
import { PrintListTableBodyData } from "./PrintListTableData";
import { getStorage } from "../../utilities/browserStorage";
import { handleModalToggle, handleOpenId, resetPrintFormValues, setActiveTab, setAppliedSearchValue, setIdToDelete, setSearchValue, setShowPrintConfigTab, showNotification } from "./printConfigSlice";
import { handleMarginApiError, handlePrinterAddEditError } from "./printConfigDataProcessor";
import i18n from "../../utilities/i18n";
import { TAB2 } from "../../utilities/staticConfigs";
import { mapMarginsToAPI } from "./constants";
const practicePK = getStorage('practice');

/**
 * Fetches printer configuration list data and formats it for the table.
 * Maps API results into `tableBodyData` for UI display.
 * Converts boolean fields like `is_target_printer` into readable values ("Yes"/"No").
 */
export const getPrinterListData = createAsyncThunk(
    "printconfig/getPrinterList",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await getPrintList(payload);

            if (!response?.data) {
                return rejectWithValue(i18n.t("systemConfigs.errormessages.printListError"));
            }

            const results = response.data.results || [];

            // Map api results into tableBodyData format
            const tableBodyData = results.map((result) =>
                PrintListTableBodyData[0].map((col) => ({
                    ...col,
                    // map api value to column
                    value: col.name === "is_target_printer"
                    ? result.is_target_printer ? "Yes" : "No"
                    : result[col.name] ?? "", 
                    id: result.id,
                }))
            );

            return {
                rawData: results, 
                tableBodyData: tableBodyData.length > 0 ? tableBodyData : PrintListTableBodyData,
            };
        } catch (error) {
            return rejectWithValue(error?.message || i18n.t("errorMessages.commonError"));
        }
    }
);

/**
 * Deletes a printer configuration item by its ID.
 */
export const removePrintListItem = createAsyncThunk(
    "printconfig/removePrintListItem",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const response = await deletePrintList(id);

            if (response?.status === 200 || response?.status === 204) {
                dispatch(getPrinterListData(practicePK));
                return;
            }

            return rejectWithValue(i18n.t("systemConfigs.errormessages.deleteError"));
        } catch (error) {
            return rejectWithValue(error?.message || i18n.t("errorMessages.commonError"));
        }
    }
);

/**
 * Fetches HCFA form coordinates for a given printer.
 * Accepts `printerId` and optional `searchValue` in the payload.
 */
export const getHCFACoordinates = createAsyncThunk(
    "printconfig/getHCFACoordinates",
    async ({ printerId, searchValue }, { rejectWithValue }) => {
        try {
            const response = await listHCFACoordinates(printerId,searchValue);
            const { status, data } = response;

            const errorMessages = {
                400: i18n.t("systemConfigs.errormessages.printerIdError"),
                500: i18n.t("systemConfigs.errormessages.serverError"),
            };

            // Handle success
            if (status === 200 && data?.records) {
                return data.records;
            }

            // Handle known error codes
            if (errorMessages[status]) {
                return rejectWithValue(errorMessages[status]);
            }

            // Handle missing data or unexpected responses
            return rejectWithValue(
                i18n.t("systemConfigs.errormessages.hcfaCoordinateError")
            );
            
        } catch (error) {
            return rejectWithValue(
                error.response?.data || i18n.t("systemConfigs.errormessages.hcfaError")
            );
        }
    }
)

/**
 * Updates HCFA form coordinates inline.Sends coordinate update data to the `updatePrintCoordinates` API.
 */
export const InlineHCFACoordinates = createAsyncThunk(
    "printconfig/inlineHCFACoordinates",
    async (datas, {dispatch, rejectWithValue }) => {
        try {
            const response = await updatePrintCoordinates(datas);

            const { data, status } = response; 

            if (status == 400) {
                return rejectWithValue(i18n.t("systemConfigs.errormessages.noValidRec"));
            }

            if (!response?.data) {
                return rejectWithValue(i18n.t("systemConfigs.errormessages.inlineCoordinateError"));
            }

            if (status == 200) {
                dispatch(setSearchValue(""));
                dispatch(setAppliedSearchValue(""));
                return data;
            }

        } catch (error) {
            return rejectWithValue(
                error.data || i18n.t("systemConfigs.errormessages.inlineError")
            );
        }
    }
);

/**
 * Updates a single HCFA coordinate and refreshes the coordinate list.
 * Sends updated field values for the selected coordinate.
 */
export const singleHCFACoordinateUpdate = createAsyncThunk(
    "printconfig/singleHCFACoordinateUpdate",
    async (payload, {getState,dispatch, rejectWithValue }) => {
        const { currentSelectedCoordinateId, cmsPrintCoordinates } = payload;
        const {printConfig} = getState();
        let id = printConfig.currOpenId
        
        try {
            const response = await updateSingleHCFACoordinates(currentSelectedCoordinateId, cmsPrintCoordinates);
            const { status, data } = response;

            const errormessages = {
                400 : i18n.t("systemConfigs.errormessages.invalidData"),
                404 : i18n.t("systemConfigs.errormessages.coordinateNotFound"),
                409 : i18n.t("systemConfigs.errormessages.conflictWhileUpdate"),
                count: i18n.t("systemConfigs.errormessages.repeatCount"),
                validInteger: i18n.t("systemConfigs.errormessages.validInteger")
            }

            if ( status == 400 && data.message === "repeat_count_max_6" ){
                return rejectWithValue(errormessages.count);
            }

            if ( status == 400 && data.message === "a valid integer is required." ){
                return rejectWithValue(errormessages.validInteger);
            }

            if (status == 200) {
                dispatch(getHCFACoordinates({ printerId: id}));
                return data.record;
            }

            if (errormessages[status]) {
                return rejectWithValue(errormessages[status]);
            }

        } catch (error) {
            return rejectWithValue(
                error.response?.data || i18n.t("systemConfigs.errormessages.inlineError")
            );
        }
    }
);

/**
 * Updates HCFA form margin settings for the selected printer.
 */
export const updateHCFAMargin = createAsyncThunk(
    "printconfig/updateHCFAMargin",
    async (payload, {getState, rejectWithValue }) => {
        const {printConfig} = getState();
        let id = printConfig.currOpenId
        let data = mapMarginsToAPI(payload);
        
        try {
            const response = await updateMargin(id, data);

            if (response?.status === 200) {
                return response.data;
            }

            if (response.status == 400) {
                const friendlyMessage = handleMarginApiError(response.data.message);
                return rejectWithValue(friendlyMessage);
            }

            if (response.status == 404) {
                return rejectWithValue(i18n.t("systemConfigs.errormessages.printerNotFount"));
            }

        } catch (error) {
            const friendlyMessage = handleMarginApiError(error, "update");
            return rejectWithValue(friendlyMessage);
        }
    }
);

/**
 * Fetches details of a single HCFA coordinate by ID.
 */
export const getSingleHCFACoordinates = createAsyncThunk(
    "printconfig/getSingleHCFACoordinates",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const response = await singleHCFACoordinates(id);

            const { status, data, error_data } = response;

            const errorMessages = {
                404 : i18n.t("systemConfigs.errormessages.missingCoordinates"),
                500 : i18n.t("systemConfigs.errormessages.unexpectedError"),
            }

             //  Success case
            if (status == 200) {
                dispatch(handleModalToggle({ modal: "cmsPrintCoordinatesModal", value: true }));
                return data;
            }

            //  Known error codes
            if (errorMessages[status]) {
                return rejectWithValue(
                error_data?.message ||
                error_data?.error_exception ||
                errorMessages[status]
                );
            }

            //  Fallback for unexpected or missing data
            return rejectWithValue(
                i18n.t("systemConfigs.errormessages.inlineCoordinateError")
            );

        } catch (error) {
            //  Network or unexpected errors
            return rejectWithValue(
                error.response?.data || i18n.t("systemConfigs.errormessages.inlineError")
            );
        }
    }
);


/**
 * Adds new printer details to the system.
 */
export const printDetails = createAsyncThunk(
    'printconfig/printDetails',
    async (payload,{dispatch, rejectWithValue}) => {
        try {
            let response = await addPrinterDetails(payload);

            const {data, status} = response

            if (status === 400 && data.message ) {
                let errorData = handlePrinterAddEditError(data.message);
                return rejectWithValue(errorData);
            }

            if (data) {
                dispatch(resetPrintFormValues({type: "printer"}))
                return response.data;
            } 
                
            return rejectWithValue(i18n.t("systemConfigs.errormessages.printError"));

        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                i18n.t("errorMessages.commonError");

            return rejectWithValue(errorMessage);
        }
    }
)

/**
 * Fetches HCFA margin values for the selected printer.
 */
export const getHCFAMarginValues = createAsyncThunk(
    "printconfig/getHCFAMarginValues",
    async (payload, {dispatch, rejectWithValue }) => {
        try {
            const response = await getMargin(payload);

            if (response?.status === 200 && response?.data) {
                dispatch(handleModalToggle({ modal: "editMarginModal", value: true }))

                return response.data;
            }

            if (response?.status === 404) {
                return rejectWithValue(i18n.t("systemConfigs.errormessages.printerNotFount"));
            }

            return rejectWithValue(i18n.t("systemConfigs.errormessages.marginError"));
        } catch (error) {
            return rejectWithValue( error.message || i18n.t("errorMessages.commonError"));
        }
    }
);

/**
 * Fetches print setting details for a given printer ID.
 */
export const getPrintSettingDetails = createAsyncThunk(
    'printconfig/getPrintSettingDetails',
    async (id, { rejectWithValue }) => {
        try {
            let response = await getPrintSettings(id);
            if (response.data) {
                return response.data;
            } 

            if (response.status === 404) {
                return rejectWithValue(i18n.t("systemConfigs.errormessages.printerNotFount"));
            } 
        
            return rejectWithValue(i18n.t("systemConfigs.errormessages.printSettingsError"));
            
        } catch (error) {
            return rejectWithValue( error.message || i18n.t("errorMessages.commonError"))
        }
    }
)

/**
 * Updates HCFA print settings for the selected printer.
 */
export const updateHCFAPrintSettings = createAsyncThunk(
    "printconfig/updateHCFAPrintSettings",
    async (id, {getState,dispatch, rejectWithValue }) => {
        const {printConfig} = getState();
        let {printFormValues} = printConfig
        
        try {
            const response = await updatePrintSettings(id, printFormValues);
            const { status, data } = response;


            if ( status === 400 && data.message) {
                const readableMessage = handlePrinterAddEditError(data.message);
                return rejectWithValue(readableMessage);
            }

            if (status === 200) {
                dispatch(handleModalToggle({ modal: "printSettingsModal", value: false }))
                dispatch(getPrinterListData(practicePK))
                return data;
            }

        return rejectWithValue(i18n.t("systemConfigs.errormessages.unexpectedPrinterError"));
        } catch (error) {
            const readableMessage = handlePrinterAddEditError(error);
            return rejectWithValue(readableMessage);
        }
    }
);

/**
 * Thunk action to handle "Edit" and "Delete" events from a dropdown menu.
 * * @param {string | number} id - The ID of the item being actioned upon.
 * @param {string} name - The name of the action to perform ("Edit", "Delete", etc.).
 */
export const handleDropDownAction = (id, name) => async (dispatch, getState) => {
    const state = getState();
    const { tableData } = state.printConfig;

    if (name === "Edit" && id) {
        dispatch(handleOpenId(id));
        await dispatch(getHCFACoordinates({ printerId: id }));
        dispatch(setActiveTab(TAB2));
        dispatch(setShowPrintConfigTab(true));
    }

    if (name === "Delete") {
        if (tableData.find((item) => item.id == id).is_target_printer) {
            dispatch(showNotification({
                status: 'error',
                message: i18n.t("systemConfigs.errormessages.NotDeletable")
            }))
            return;
        }
        dispatch(setIdToDelete(id));

        dispatch(handleModalToggle({ modal: "showDeleteModalWindow", value: true }));
    }
};